import { useEffect, useRef, useState, useMemo } from "react";
import {
  createChart, CandlestickSeries, LineSeries, LineStyle, LineType,
  CrosshairMode, createSeriesMarkers,
} from "lightweight-charts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Play, Pause, RotateCcw, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const toTs = (iso) => (iso ? Math.floor(Date.parse(iso) / 1000) + 19800 : null);
const snap = (t) => (t === null ? null : t - ((t - 19800) % 60));
const fmtN = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }));
const fmtClock = (t) => new Date((t - 19800) * 1000).toLocaleTimeString("en-IN", { hour12: false, timeZone: "Asia/Kolkata" });

const EVENT_COLORS = {
  ENTRY: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  ITM_ACTIVATED: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  TSL_UPDATE: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  EXIT: "text-rose-400 bg-rose-500/10 border-rose-500/20",
};

const SPEEDS = [2, 6, 15];

export const TradeReplayModal = ({ open, onClose, trade }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const slSeriesRef = useRef(null);
  const markersRef = useRef(null);
  const [tab, setTab] = useState("option");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealIdx, setRevealIdx] = useState(null); // null = show everything
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(SPEEDS[1]);
  const [chartReady, setChartReady] = useState(0);

  // Fetch replay payload
  useEffect(() => {
    if (!open || !trade?.id) return;
    setData(null); setError(null); setTab("option");
    setRevealIdx(null); setPlaying(false); setLoading(true);
    api.get(`/morning-tea/trades/${trade.id}/replay`)
      .then((res) => setData(res.data.data))
      .catch((err) => setError(err.response?.data?.detail || "Failed to load replay data"))
      .finally(() => setLoading(false));
  }, [open, trade?.id]);

  const view = useMemo(() => {
    if (!data) return null;
    const t = data.trade;
    const entryTs = snap(toTs(t.entry_time));
    const exitTs = snap(toTs(t.exit_time));
    const itmTs = snap(toTs(t.itm_time));
    const confirmTs = snap(toTs(t.confirmation_time));
    if (tab === "option") {
      const candles = data.option.candles;
      const markers = [
        entryTs && { time: entryTs, position: "belowBar", color: "#10b981", shape: "arrowUp", text: `BUY ₹${fmtN(t.entry_price)}` },
        itmTs && { time: itmTs, position: "aboveBar", color: "#f59e0b", shape: "circle", text: "ITM" },
        exitTs && { time: exitTs, position: "aboveBar", color: "#f43f5e", shape: "arrowDown", text: `EXIT ₹${fmtN(t.exit_price)}` },
      ].filter(Boolean).sort((a, b) => a.time - b.time);
      const lines = [
        { price: t.entry_price, color: "#22d3ee", title: "Entry", dashed: false },
        t.initial_sl && { price: t.initial_sl, color: "#f43f5e", title: "Init SL", dashed: true },
        { price: t.itm_trigger, color: "#f59e0b", title: "ITM Trig", dashed: true },
      ].filter(Boolean);
      // SL step path (snap + dedupe, extend to exit/last candle)
      const dedup = new Map();
      (data.sl_path || []).forEach((p) => dedup.set(snap(p.time), p.value));
      let slPath = [...dedup.entries()].map(([time, value]) => ({ time, value })).sort((a, b) => a.time - b.time);
      const lastCandle = candles[candles.length - 1];
      if (slPath.length && lastCandle && lastCandle.time > slPath[slPath.length - 1].time) {
        const end = exitTs && exitTs <= lastCandle.time ? exitTs : lastCandle.time;
        if (end > slPath[slPath.length - 1].time) slPath = [...slPath, { time: end, value: slPath[slPath.length - 1].value }];
      }
      return { candles, markers, lines, slPath, source: data.option.source, message: data.option.message, label: t.option_symbol };
    }
    const candles = data.underlying.candles;
    const markers = [
      confirmTs && { time: confirmTs, position: "belowBar", color: "#22d3ee", shape: "circle", text: `${t.key_pivot_name} break` },
      entryTs && { time: entryTs, position: "belowBar", color: "#10b981", shape: "arrowUp", text: "ENTRY" },
      exitTs && { time: exitTs, position: "aboveBar", color: "#f43f5e", shape: "arrowDown", text: "EXIT" },
    ].filter(Boolean).sort((a, b) => a.time - b.time);
    const lines = [
      { price: t.key_pivot_value, color: "#22d3ee", title: t.key_pivot_name || "KP", dashed: false },
      t.initial_sl_underlying && { price: t.initial_sl_underlying, color: "#f43f5e", title: "SL (U)", dashed: true },
    ].filter(Boolean);
    return { candles, markers, lines, slPath: [], source: data.underlying.source, message: data.underlying.message, label: t.symbol };
  }, [data, tab]);

  // Create / destroy chart
  useEffect(() => {
    if (!open || !view) return;
    let chart;
    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      chart = createChart(containerRef.current, {
        autoSize: true,
        localization: { locale: "en-IN" },
        layout: { background: { color: "#0c0d10" }, textColor: "#94a3b8", fontSize: 11 },
        grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
        crosshair: { mode: CrosshairMode.Normal },
        timeScale: { timeVisible: true, secondsVisible: false, borderColor: "rgba(255,255,255,0.1)" },
        rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
      });
      const series = chart.addSeries(CandlestickSeries, {
        upColor: "#10b981", downColor: "#f43f5e",
        borderUpColor: "#10b981", borderDownColor: "#f43f5e",
        wickUpColor: "#10b981", wickDownColor: "#f43f5e",
      });
      view.lines.forEach((l) => {
        if (!(l.price > 0)) return;
        series.createPriceLine({
          price: Number(l.price), color: l.color, lineWidth: 2,
          lineStyle: l.dashed ? LineStyle.Dashed : LineStyle.Solid,
          axisLabelVisible: true, title: l.title,
        });
      });
      const slSeries = chart.addSeries(LineSeries, {
        color: "#f43f5e", lineWidth: 1, lineStyle: LineStyle.Dashed,
        lineType: LineType.WithSteps, priceLineVisible: false, lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      chartRef.current = chart;
      candleSeriesRef.current = series;
      slSeriesRef.current = slSeries;
      markersRef.current = createSeriesMarkers(series, []);
      setChartReady((c) => c + 1); // trigger data push now that series exist
    }, 60);
    return () => {
      clearTimeout(timer);
      if (chartRef.current) { chartRef.current.remove(); }
      chartRef.current = null; candleSeriesRef.current = null;
      slSeriesRef.current = null; markersRef.current = null;
    };
  }, [open, view]);

  // Push (sliced) data
  useEffect(() => {
    if (!candleSeriesRef.current || !view?.candles?.length) return;
    const n = view.candles.length;
    const idx = revealIdx === null ? n : Math.min(revealIdx, n);
    const slice = view.candles.slice(0, Math.max(idx, 1));
    const lastTime = slice[slice.length - 1].time;
    candleSeriesRef.current.setData(slice);
    slSeriesRef.current?.setData(view.slPath.filter((p) => p.time <= lastTime));
    markersRef.current?.setMarkers(view.markers.filter((m) => m.time <= lastTime));
    chartRef.current?.timeScale().fitContent();
  }, [view, revealIdx, chartReady]);

  // Playback loop
  useEffect(() => {
    if (!playing || !view?.candles?.length) return;
    const id = setInterval(() => {
      setRevealIdx((i) => {
        const next = (i === null ? 0 : i) + speed;
        if (next >= view.candles.length) { setPlaying(false); return null; }
        return next;
      });
    }, 120);
    return () => clearInterval(id);
  }, [playing, speed, view]);

  const t = data?.trade || trade;
  const progress = view?.candles?.length
    ? (revealIdx === null ? view.candles.length : Math.min(revealIdx, view.candles.length)) / view.candles.length : 0;
  const curTime = view?.candles?.length
    ? fmtClock(view.candles[Math.max((revealIdx === null ? view.candles.length : Math.min(revealIdx, view.candles.length)) - 1, 0)].time) : "";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setPlaying(false); onClose(); } }}>
      <DialogContent className="max-w-5xl bg-[#0c0d10] border-white/10 p-0 gap-0" data-testid="trade-replay-modal">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-white/10">
          <div className="flex flex-wrap items-center justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="text-white text-base flex items-center gap-2.5" data-testid="replay-modal-title">
                Replay · {t?.option_symbol}
                {view?.source === "DEMO" && (
                  <span className="text-[9px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5">DEMO DATA</span>
                )}
                <span className={`text-sm font-mono ${(t?.gross_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {t?.gross_pnl !== null && t?.gross_pnl !== undefined ? `₹${Number(t.gross_pnl).toLocaleString("en-IN")}` : "OPEN"}
                </span>
              </DialogTitle>
              <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
                {t?.date} · {t?.symbol} {t?.direction} · KP {t?.key_pivot_name} ₹{fmtN(t?.key_pivot_value)} · Entry ₹{fmtN(t?.entry_price)}{t?.exit_price ? ` → Exit ₹${fmtN(t.exit_price)} (${(t.exit_reason || "").replace("EXIT_", "")})` : ""}
              </p>
            </div>
            <div className="flex items-center gap-0.5 p-0.5 bg-black/40 rounded border border-white/10">
              {["option", "underlying"].map((v) => (
                <button key={v} data-testid={`replay-tab-${v}`} onClick={() => { setPlaying(false); setRevealIdx(null); setTab(v); }}
                  className={`px-2.5 py-1 rounded text-[10px] font-mono uppercase transition-all ${
                    tab === v ? "bg-cyan-500/20 text-cyan-400" : "text-slate-500 hover:text-slate-300"}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="relative">
          <div ref={containerRef} className="w-full h-[400px]" data-testid="replay-chart-container" />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
            </div>
          )}
          {!loading && (error || (view && !view.candles.length)) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-4 py-2" data-testid="replay-error">
                {error || view?.message || "No candle data available for this date"}
              </div>
            </div>
          )}
        </div>

        {/* Playback controls */}
        <div className="px-5 py-2.5 border-t border-white/10 flex items-center gap-3">
          <button data-testid="replay-play-btn" disabled={!view?.candles?.length}
            onClick={() => {
              if (playing) { setPlaying(false); return; }
              if (revealIdx === null) setRevealIdx(1);
              setPlaying(true);
            }}
            className="flex items-center gap-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-40">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {playing ? "Pause" : "Replay"}
          </button>
          <button data-testid="replay-reset-btn" onClick={() => { setPlaying(false); setRevealIdx(null); }}
            className="flex items-center gap-1 text-zinc-400 hover:text-white border border-white/10 rounded px-2 py-1.5 text-[10px] font-mono transition-colors" title="Show full session">
            <RotateCcw className="w-3 h-3" /> Full
          </button>
          <div className="flex items-center gap-0.5 p-0.5 bg-black/40 rounded border border-white/10">
            {SPEEDS.map((s, i) => (
              <button key={s} data-testid={`replay-speed-${i}`} onClick={() => setSpeed(s)}
                className={`px-2 py-0.5 rounded text-[10px] font-mono transition-all ${
                  speed === s ? "bg-cyan-500/20 text-cyan-400" : "text-slate-500 hover:text-slate-300"}`}>
                {["1x", "3x", "8x"][i]}
              </button>
            ))}
          </div>
          <div className="flex-1 h-1 rounded bg-white/5 overflow-hidden">
            <div className="h-full bg-cyan-500/60 transition-[width] duration-150" style={{ width: `${progress * 100}%` }} />
          </div>
          <span className="text-[10px] font-mono text-slate-500 w-14 text-right">{curTime}</span>
        </div>

        {/* Event timeline */}
        {t?.events?.length > 0 && (
          <div className="px-5 py-3 border-t border-white/10 max-h-[140px] overflow-y-auto" data-testid="replay-events">
            {t.events.map((ev, i) => (
              <div key={i} className="flex items-start gap-2.5 py-1 text-[11px] font-mono">
                <span className="text-zinc-600 shrink-0">{(ev.time || "").slice(11, 19)}</span>
                <span className={`shrink-0 px-1.5 py-px rounded border text-[9px] ${EVENT_COLORS[ev.type] || "text-zinc-400 bg-zinc-500/10 border-zinc-500/20"}`}>
                  {ev.type}
                </span>
                <span className="text-zinc-400">{ev.detail}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
